/**
 * Area `app-sign-in` of the console gateway — over `apps/api/src/routes/app-auth-session/` (plan-034 WAPP-APPSIGNIN;
 * design §45.4, §45.7.1; ADR 0089).
 *
 *   createAppAuthorizationCode   `createAppAuthorizationCode`   the consent page's "Continue": the browser's cookie and
 *                                                               CSRF token mint the 60-second app authorization code
 *
 * Only the consent page (`../../routes/app-sign-in/`) calls it. The answer's `return_uri` is the api's configured exact
 * return address for the platform; the page never builds one from its own query, so it cannot be steered elsewhere.
 * The code is handed to the page and to nothing else: it is never logged, cached in a query or written to storage.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import type { DevicePlatform } from "@wetalk/domain/identity/client-kind";

import type { ConsoleGateway } from "../gateway.js";
import { readRecord, readText } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** What "Continue" sends: the app's S256 challenge, its platform and D-25's fact. */
export interface AppAuthorizationCodeRequestView {
  /** The app's PKCE challenge, 43 base64url characters; the method is always `S256`. */
  readonly codeChallenge: string;
  readonly devicePlatform: DevicePlatform;
  /** `true` when the browser sign-in was made on the way, for the app (owner decision D-25). */
  readonly endsBrowserAuthSession: boolean;
}

/** The api's answer: the code, until when it may be exchanged, and where the page sends it. */
export interface AppAuthorizationCodeView {
  readonly code: string;
  readonly expiresAt: string;
  readonly returnUri: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * Mints the app authorization code with this browser's sign-in. Refusals arrive as `ApiError`: `422
     * app_sign_in_unavailable`, `403 app_sign_in_refused`, `401 assurance_required`, `403 network_not_allowed`.
     */
    createAppAuthorizationCode(request: AppAuthorizationCodeRequestView): Promise<AppAuthorizationCodeView>;
  }
}

export type AppSignInGatewaySlice = Pick<ConsoleGateway, "createAppAuthorizationCode">;

/** This area's rows of `GATEWAY_GAP`: none — the operation is in API-APPAUTH's fragment. */
export const appSignInGap: readonly GatewayGap[] = [];

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

export function appAuthorizationCodeOfWire(result: ApiResult): AppAuthorizationCodeView {
  const data = readRecord(readRecord(assertOk(result), "createAppAuthorizationCode")["data"], "createAppAuthorizationCode.data");
  return {
    code: readText(data["code"], "createAppAuthorizationCode.data.code"),
    expiresAt: readText(data["expires_at"], "createAppAuthorizationCode.data.expires_at"),
    returnUri: readText(data["return_uri"], "createAppAuthorizationCode.data.return_uri"),
  };
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function appSignInApiSlice(client: WeTalkApiClient, _context: SliceContext): AppSignInGatewaySlice {
  return {
    async createAppAuthorizationCode(request) {
      const result = await client.createAppAuthorizationCode({
        body: {
          code_challenge: request.codeChallenge,
          code_challenge_method: "S256",
          device_platform: request.devicePlatform,
          ends_browser_auth_session: request.endsBrowserAuthSession,
        },
      });
      return appAuthorizationCodeOfWire(result);
    },
  };
}
